import { supabase } from "@/lib/supabase/client";
import { useAuthStore } from "@/state/authStore";

export type CoachTipContext = {
  workoutId: string;
  weekStart: string;
  workoutName: string;
  workoutType: string;
  durationMin: number;
  ftpWatts?: number;
  readiness?: number;
  tsb?: number;
  /** Chronic Training Load — long-term fitness (42-day EMA of TSS). */
  ctl?: number;
  /** Acute Training Load — short-term fatigue (7-day EMA of TSS). */
  atl?: number;
  /** Fraction of workouts completed vs skipped over last 4 weeks (0–1). */
  completionRate?: number;
  /** Total distinct weeks of training data (consistency proxy). */
  weekCount?: number;
  /**
   * Human-readable target label derived from actual workout blocks,
   * e.g. "148-162W  ·  RPE 3-4/10". Prevents the AI from hallucinating watt values.
   */
  workoutTargetLabel?: string;
  daysSinceLastRide?: number;
  phase: string;
  daysToEvent?: number;
  avgRpe?: number;
  /** Whether this is race week (daysToEvent ≤ 7). */
  isRaceWeek?: boolean;
  /** Athlete experience level from profile. */
  level?: string;
  /** Day classification derived from workout type. */
  dayClassification?: "hard" | "recovery" | "moderate" | "long";
  /** Last 4 completed workout types as a comma-separated string. */
  recentWorkoutTypes?: string;
  /** "workout" = today card tip (default); "report" = single-sentence note; "report-full" = all 4 coaching fields. */
  mode?: "workout" | "report" | "report-full";
};

/** Structured response from the report-full Groq call. */
export type AiCoachReport = {
  coachNote: string;
  recommendedToday: string;
  ifYouFeelGood: string;
  avoid: string | null;
};

/** Context for the weekly plan tip (called after Feeling Lucky / Plan Next Week). */
export type WeeklyPlanContext = {
  weekStart: string;
  /** Comma-separated workout types for the generated week, e.g. "endurance, threshold, recovery". */
  workoutTypes: string;
  phase: string;
  ftpWatts?: number;
  tsb?: number;
  ctl?: number;
  daysToEvent?: number;
  isRaceWeek?: boolean;
  recentWorkoutTypes?: string;
};

/** Context for the post-ride reflective coaching note. */
export type PostRideContext = {
  workoutId: string;
  workoutName: string;
  workoutType: string;
  rpe?: number;
  actualDurationMinutes?: number;
  ftpWatts?: number;
  phase: string;
  tsb?: number;
  dayClassification?: "hard" | "recovery" | "moderate" | "long";
};

// ── Cache helpers ────────────────────────────────────────────────────────────

/** Try sessionStorage first, then localStorage as fallback. Returns null if both fail. */
function readCache(key: string): string | null {
  try {
    const ss = sessionStorage.getItem(key);
    if (ss) return ss;
  } catch { /* unavailable */ }
  try {
    return localStorage.getItem(key);
  } catch { /* unavailable */ }
  return null;
}

/** Write to sessionStorage; fall back to localStorage on quota errors. */
function writeCache(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
    return;
  } catch { /* quota or unavailable */ }
  try {
    localStorage.setItem(key, value);
  } catch { /* quota or unavailable */ }
}

// ── coachFetch ───────────────────────────────────────────────────────────────

/**
 * Thin wrapper around fetch that automatically adds the Supabase Bearer token.
 * All calls to generate-coach-tip must go through here so the Netlify function
 * can verify the request comes from an authenticated user.
 */
async function coachFetch(body: Record<string, unknown>): Promise<Response> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`;
    }
  } catch { /* not logged in or supabase unavailable */ }
  return fetch("/.netlify/functions/generate-coach-tip", {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
}

// ── fetchCoachReport ─────────────────────────────────────────────────────────

/**
 * Fetch all 4 AI coaching fields for the History page Coach Report in a single call.
 * Cached in sessionStorage (fallback: localStorage) keyed on athlete state + week start
 * so it refreshes each time the training state changes.
 *
 * Returns null on any error — callers fall back to rule-based text.
 */
export async function fetchCoachReport(
  context: Omit<
    CoachTipContext,
    | "workoutId"
    | "workoutType"
    | "durationMin"
    | "workoutTargetLabel"
  > & {
    athleteState: string;
    ctlDelta?: number;
    ftpDelta?: number;
    daysSinceTest?: number;
    streak?: number;
    weekSessionsCompleted?: number;
    weekSessionsPlanned?: number;
  },
): Promise<AiCoachReport | null> {
  const uid = useAuthStore.getState().account?.id ?? "anon";
  const cacheKey = `coach-report-full-${uid}-${context.athleteState}-${context.weekStart}-${Math.round(context.tsb ?? 0)}-${Math.round(context.ctl ?? 0)}-${context.weekSessionsCompleted ?? 0}of${context.weekSessionsPlanned ?? 0}`;
  const cached = readCache(cacheKey);
  if (cached) {
    try {
      return JSON.parse(cached) as AiCoachReport;
    } catch { /* corrupt entry — fetch fresh */ }
  }

  try {
    const response = await coachFetch({
      workoutName: context.workoutName,
      workoutType: "summary",
      durationMin: 60,
      ftpWatts: context.ftpWatts,
      readiness: context.readiness,
      tsb: context.tsb,
      ctl: context.ctl,
      atl: context.atl,
      completionRate: context.completionRate,
      weekCount: context.weekCount,
      daysSinceLastRide: context.daysSinceLastRide,
      phase: context.phase,
      daysToEvent: context.daysToEvent,
      avgRpe: context.avgRpe,
      ctlDelta: context.ctlDelta,
      ftpDelta: context.ftpDelta,
      daysSinceTest: context.daysSinceTest,
      streak: context.streak,
      weekSessionsCompleted: context.weekSessionsCompleted,
      weekSessionsPlanned: context.weekSessionsPlanned,
      mode: "report-full",
    });

    if (!response.ok) return null;

    const data = (await response.json()) as Partial<AiCoachReport>;
    if (!data.coachNote || !data.recommendedToday || !data.ifYouFeelGood)
      return null;

    const report: AiCoachReport = {
      coachNote: data.coachNote,
      recommendedToday: data.recommendedToday,
      ifYouFeelGood: data.ifYouFeelGood,
      avoid: data.avoid ?? null,
    };

    writeCache(cacheKey, JSON.stringify(report));
    return report;
  } catch {
    return null;
  }
}

// ── fetchCoachTip ────────────────────────────────────────────────────────────

/**
 * Fetch an AI-generated coaching note from the Groq-backed Netlify Function.
 * Results are cached in sessionStorage (fallback: localStorage) so we only
 * call the API once per workout per browser session.
 *
 * Returns null on any error so callers can fall back to the rule-based tip.
 */
export async function fetchCoachTip(
  context: CoachTipContext,
): Promise<string | null> {
  // Include workout name slug + readiness + ftpWatts so plan/readiness/FTP changes bust the cache.
  const namePart = context.workoutName.slice(0, 24).replace(/\s+/g, "-");
  const cacheKey = `coach-tip-${context.workoutId}-${context.weekStart}-${namePart}-${context.readiness ?? 0}-${context.ftpWatts ?? 0}`;

  const cached = readCache(cacheKey);
  if (cached) return cached;

  try {
    const response = await coachFetch({
      workoutName: context.workoutName,
      workoutType: context.workoutType,
      durationMin: context.durationMin,
      ftpWatts: context.ftpWatts,
      readiness: context.readiness,
      tsb: context.tsb,
      ctl: context.ctl,
      atl: context.atl,
      completionRate: context.completionRate,
      weekCount: context.weekCount,
      workoutTargetLabel: context.workoutTargetLabel,
      daysSinceLastRide: context.daysSinceLastRide,
      phase: context.phase,
      daysToEvent: context.daysToEvent,
      avgRpe: context.avgRpe,
      isRaceWeek: context.isRaceWeek,
      level: context.level,
      dayClassification: context.dayClassification,
      recentWorkoutTypes: context.recentWorkoutTypes,
      mode: context.mode,
    });

    if (!response.ok) return null;

    const data = (await response.json()) as { tip?: unknown };
    const tip = typeof data.tip === "string" ? data.tip.trim() : null;
    if (!tip) return null;

    writeCache(cacheKey, tip);
    return tip;
  } catch {
    // Network error, function not deployed yet, etc.
    return null;
  }
}

// ── fetchWeeklyPlanTip ───────────────────────────────────────────────────────

/**
 * Fetch a 2-3 sentence AI coaching note explaining the logic behind the
 * generated week's training structure. Called after "Feeling Lucky" or
 * "Plan Next Week" generates a plan.
 *
 * Returns null on any error.
 */
export async function fetchWeeklyPlanTip(
  context: WeeklyPlanContext,
): Promise<string | null> {
  // Include workoutTypes in key so regenerating a different plan busts the cache.
  const typeSlug = context.workoutTypes.replace(/[\s,]+/g, "-").slice(0, 40);
  const cacheKey = `weekly-plan-tip-${context.weekStart}-${typeSlug}`;

  const cached = readCache(cacheKey);
  if (cached) return cached;

  try {
    const response = await coachFetch({
      workoutName: `Weekly plan: ${context.workoutTypes}`,
      workoutType: context.workoutTypes,
      durationMin: 60,
      phase: context.phase,
      ftpWatts: context.ftpWatts,
      tsb: context.tsb,
      ctl: context.ctl,
      daysToEvent: context.daysToEvent,
      isRaceWeek: context.isRaceWeek,
      recentWorkoutTypes: context.recentWorkoutTypes,
      mode: "weekly-plan",
    });

    if (!response.ok) return null;

    const data = (await response.json()) as { tip?: unknown };
    const tip = typeof data.tip === "string" ? data.tip.trim() : null;
    if (!tip) return null;

    writeCache(cacheKey, tip);
    return tip;
  } catch {
    return null;
  }
}

// ── fetchWeeklyRecapTip ──────────────────────────────────────────────────────

/** Context for the end-of-week retrospective coaching note shown in Recent Notes. */
export type WeeklyRecapContext = {
  weekStart: string;
  /** Today's date "YYYY-MM-DD" — busts the cache each new calendar day. */
  today: string;
  /** Comma-separated session types that were completed that week. */
  workoutTypes: string;
  /** Comma-separated actual workout names planned for the week, e.g. "Sweet Spot Progression, Threshold Cruise". */
  workoutNames: string;
  completedCount: number;
  skippedCount: number;
  totalCount: number;
  phase: string;
  /** Average perceived effort (1–5 scale) from completed sessions. */
  avgRpe?: number;
};

/**
 * Fetch a 2-sentence AI coaching recap for a past training week.
 * Cached per week keyed on completions so it refreshes if the user logs more rides.
 *
 * Returns null on any error.
 */
export async function fetchWeeklyRecapTip(
  context: WeeklyRecapContext,
): Promise<string | null> {
  const typeSlug = context.workoutTypes.replace(/[\s,]+/g, "-").slice(0, 40);
  const cacheKey = `weekly-recap-${context.weekStart}-${context.today}-${typeSlug}-${context.completedCount}-${context.skippedCount}-${Math.round(context.avgRpe ?? 0)}`;

  const cached = readCache(cacheKey);
  if (cached) return cached;

  try {
    const response = await coachFetch({
      workoutName: `Week of ${context.weekStart}`,
      workoutType: context.workoutTypes,
      durationMin: 60,
      phase: context.phase,
      avgRpe: context.avgRpe,
      today: context.today,
      workoutNames: context.workoutNames,
      completedCount: context.completedCount,
      skippedCount: context.skippedCount,
      totalCount: context.totalCount,
      mode: "weekly-recap",
    });

    if (!response.ok) return null;

    const data = (await response.json()) as { tip?: unknown };
    const tip = typeof data.tip === "string" ? data.tip.trim() : null;
    if (!tip) return null;

    writeCache(cacheKey, tip);
    return tip;
  } catch {
    return null;
  }
}

// ── fetchCheckInResponse ──────────────────────────────────────────────────────

export async function fetchCheckInResponse(context: {
  weekStart: string;
  sleepScore: number;
  notes?: string;
  phase: string;
  tsb?: number;
  ctl?: number;
  atl?: number;
  ftpWatts?: number;
}): Promise<string | null> {
  const cacheKey = `check-in-ai-${context.weekStart}-${context.sleepScore}`;
  const cached = readCache(cacheKey);
  if (cached) return cached;
  try {
    const response = await coachFetch({
      workoutName: "check-in",
      sleepScore: context.sleepScore,
      notes: context.notes,
      phase: context.phase,
      tsb: context.tsb,
      ctl: context.ctl,
      atl: context.atl,
      ftpWatts: context.ftpWatts,
      mode: "check-in-response",
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { tip?: unknown };
    const tip = typeof data.tip === "string" ? data.tip.trim() : null;
    if (!tip) return null;
    writeCache(cacheKey, tip);
    return tip;
  } catch {
    return null;
  }
}

// ── fetchRaceBrief ────────────────────────────────────────────────────────────

export type AiRaceBrief = { raceNote: string; keyFocus: string };

export async function fetchRaceBrief(context: {
  eventId: string;
  eventName: string;
  eventType: string;
  daysToEvent: number;
  distanceKm?: number;
  terrainProfile?: string;
  ftpWatts?: number;
  tsb?: number;
  ctl?: number;
  atl?: number;
  phase: string;
}): Promise<AiRaceBrief | null> {
  const cacheKey = `race-brief-${context.eventId}-${context.daysToEvent}`;
  const cached = readCache(cacheKey);
  if (cached) {
    try { return JSON.parse(cached) as AiRaceBrief; } catch { /* stale */ }
  }
  try {
    const response = await coachFetch({
      workoutName: "race-brief",
      eventName: context.eventName,
      eventType: context.eventType,
      daysToEvent: context.daysToEvent,
      distanceKm: context.distanceKm,
      terrainProfile: context.terrainProfile,
      ftpWatts: context.ftpWatts,
      tsb: context.tsb,
      ctl: context.ctl,
      atl: context.atl,
      phase: context.phase,
      mode: "race-brief",
    });
    if (!response.ok) return null;
    const data = (await response.json()) as Partial<AiRaceBrief>;
    if (!data.raceNote || !data.keyFocus) return null;
    writeCache(cacheKey, JSON.stringify(data));
    return { raceNote: data.raceNote, keyFocus: data.keyFocus };
  } catch {
    return null;
  }
}

// ── fetchFtpTestPrompt ────────────────────────────────────────────────────────

export async function fetchFtpTestPrompt(context: {
  ftpWatts: number;
  ctlGain: number;
  daysSinceTest: number;
  recentThresholdRpe?: number;
  tsb?: number;
  ctl?: number;
}): Promise<string | null> {
  const cacheKey = `ftp-prompt-${Math.round(context.ctlGain)}-${context.daysSinceTest}`;
  const cached = readCache(cacheKey);
  if (cached) return cached;
  try {
    const response = await coachFetch({
      workoutName: "ftp-prompt",
      ftpWatts: context.ftpWatts,
      ctlGain: context.ctlGain,
      daysSinceTest: context.daysSinceTest,
      recentThresholdRpe: context.recentThresholdRpe,
      tsb: context.tsb,
      ctl: context.ctl,
      phase: "build",
      mode: "ftp-prompt",
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { tip?: unknown };
    const tip = typeof data.tip === "string" ? data.tip.trim() : null;
    if (!tip) return null;
    writeCache(cacheKey, tip);
    return tip;
  } catch {
    return null;
  }
}

// ── fetchSwapSuggestion ───────────────────────────────────────────────────────

export async function fetchSwapSuggestion(context: {
  workoutId: string;
  skippedWorkoutName: string;
  skippedWorkoutType: string;
  skippedDurationMin: number;
  phase: string;
  tsb?: number;
  ctl?: number;
  atl?: number;
  ftpWatts?: number;
  weekWorkoutTypes: string;
  readiness?: number;
}): Promise<string | null> {
  const cacheKey = `swap-${context.workoutId}-${Math.round(context.tsb ?? 0)}`;
  const cached = readCache(cacheKey);
  if (cached) return cached;
  try {
    const response = await coachFetch({
      workoutName: "swap-suggest",
      skippedWorkoutName: context.skippedWorkoutName,
      skippedWorkoutType: context.skippedWorkoutType,
      skippedDurationMin: context.skippedDurationMin,
      phase: context.phase,
      tsb: context.tsb,
      ctl: context.ctl,
      atl: context.atl,
      ftpWatts: context.ftpWatts,
      weekWorkoutTypes: context.weekWorkoutTypes,
      readiness: context.readiness,
      mode: "swap-suggest",
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { tip?: unknown };
    const tip = typeof data.tip === "string" ? data.tip.trim() : null;
    if (!tip) return null;
    writeCache(cacheKey, tip);
    return tip;
  } catch {
    return null;
  }
}

// ── fetchCoachChat ────────────────────────────────────────────────────────────

export type ChatResult =
  | { ok: true; reply: string }
  | { ok: false; error: string };

export async function fetchCoachChat(
  message: string,
  history: Array<{ role: "user" | "assistant"; content: string }>,
  context: {
    ftpWatts?: number;
    tsb?: number;
    ctl?: number;
    atl?: number;
    phase: string;
    daysToEvent?: number;
    readiness?: number;
    currentWorkoutType?: string;
    recentRides?: Array<{
      name: string;
      type: string;
      durationMin: number;
      rpe?: number;
      powerAvgWatts?: number;
      powerNpWatts?: number;
      heartRateAvg?: number;
      cadenceAvg?: number;
      distanceKm?: number;
      tssScore?: number;
      speedAvgKph?: number;
      completedAt: string;
      notes?: string;
    }>;
    weekPlan?: Array<{ name: string; type: string; day: string; completed: boolean }>;
  },
): Promise<ChatResult> {
  try {
    const response = await coachFetch({
      workoutName: "chat",
      message,
      conversationHistory: history,
      ftpWatts: context.ftpWatts,
      tsb: context.tsb,
      ctl: context.ctl,
      atl: context.atl,
      phase: context.phase,
      daysToEvent: context.daysToEvent,
      readiness: context.readiness,
      workoutType: context.currentWorkoutType,
      recentRides: context.recentRides,
      weekPlan: context.weekPlan,
      mode: "chat",
    });
    if (!response.ok) {
      if (response.status === 401) return { ok: false, error: "Session expired — try refreshing the page." };
      if (response.status === 429) return { ok: false, error: "Too many requests — wait a minute and try again." };
      console.error("[CoachChat] API error:", response.status);
      return { ok: false, error: "Coach is unavailable right now. Try again shortly." };
    }
    const data = (await response.json()) as { tip?: unknown };
    if (typeof data.tip === "string" && data.tip.trim()) {
      return { ok: true, reply: data.tip.trim() };
    }
    return { ok: false, error: "Coach returned an empty response. Try rephrasing." };
  } catch (err) {
    console.error("[CoachChat] Network error:", err);
    return { ok: false, error: "Network error — check your connection." };
  }
}

// ── fetchPowerRideTip ─────────────────────────────────────────────────────────

export async function fetchPowerRideTip(context: {
  workoutId: string;
  workoutName: string;
  workoutType: string;
  durationMin: number;
  powerNpWatts?: number;
  powerAvgWatts?: number;
  tssScore?: number;
  cadenceAvg?: number;
  heartRateAvg?: number;
  ascentM?: number;
  bestEffortsStr?: string;
  ftpWatts?: number;
  phase: string;
  tsb?: number;
  perceivedEffort?: number;
}): Promise<string | null> {
  const cacheKey = `power-ride-tip-${context.workoutId}`;
  const cached = readCache(cacheKey);
  if (cached) return cached;
  try {
    const response = await coachFetch({
      workoutName: context.workoutName,
      workoutType: context.workoutType,
      durationMin: context.durationMin,
      powerNpWatts: context.powerNpWatts,
      powerAvgWatts: context.powerAvgWatts,
      tssScore: context.tssScore,
      cadenceAvg: context.cadenceAvg,
      heartRateAvg: context.heartRateAvg,
      ascentM: context.ascentM,
      bestEffortsStr: context.bestEffortsStr,
      ftpWatts: context.ftpWatts,
      phase: context.phase,
      tsb: context.tsb,
      avgRpe: context.perceivedEffort,
      mode: "post-ride-power",
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { tip?: unknown };
    const tip = typeof data.tip === "string" ? data.tip.trim() : null;
    if (!tip) return null;
    writeCache(cacheKey, tip);
    return tip;
  } catch {
    return null;
  }
}

// ── fetchPrCommentary ─────────────────────────────────────────────────────────

export async function fetchPrCommentary(context: {
  workoutId: string;
  prType: string;
  newWatts: number;
  oldWatts?: number;
  ftpWatts?: number;
  phase: string;
  tsb?: number;
}): Promise<string | null> {
  const cacheKey = `pr-commentary-${context.workoutId}-${context.prType.replace(/\s/g, "-")}`;
  const cached = readCache(cacheKey);
  if (cached) return cached;
  try {
    const response = await coachFetch({
      workoutName: "pr-commentary",
      prType: context.prType,
      newWatts: context.newWatts,
      oldWatts: context.oldWatts,
      ftpWatts: context.ftpWatts,
      phase: context.phase,
      tsb: context.tsb,
      mode: "pr-commentary",
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { tip?: unknown };
    const tip = typeof data.tip === "string" ? data.tip.trim() : null;
    if (!tip) return null;
    writeCache(cacheKey, tip);
    return tip;
  } catch {
    return null;
  }
}

// ── fetchEventPrep ────────────────────────────────────────────────────────────

export type AiEventPrep = { prepNote: string; phaseHint: string };

export async function fetchEventPrep(context: {
  eventId: string;
  eventName: string;
  eventType: string;
  daysToEvent: number;
  distanceKm?: number;
  elevationGain?: number;
  terrainProfile?: string;
  ftpWatts?: number;
  ctl?: number;
  phase: string;
}): Promise<AiEventPrep | null> {
  const cacheKey = `event-prep-${context.eventId}-${Math.round(context.daysToEvent / 7)}`;
  const cached = readCache(cacheKey);
  if (cached) {
    try { return JSON.parse(cached) as AiEventPrep; } catch { /* stale */ }
  }
  try {
    const response = await coachFetch({
      workoutName: "event-prep",
      eventName: context.eventName,
      eventType: context.eventType,
      daysToEvent: context.daysToEvent,
      distanceKm: context.distanceKm,
      elevationGain: context.elevationGain,
      terrainProfile: context.terrainProfile,
      ftpWatts: context.ftpWatts,
      ctl: context.ctl,
      phase: context.phase,
      mode: "event-prep",
    });
    if (!response.ok) return null;
    const data = (await response.json()) as Partial<AiEventPrep>;
    if (!data.prepNote || !data.phaseHint) return null;
    writeCache(cacheKey, JSON.stringify(data));
    return { prepNote: data.prepNote, phaseHint: data.phaseHint };
  } catch {
    return null;
  }
}

// ── fetchAnalyticsInsight ─────────────────────────────────────────────────────

export async function fetchAnalyticsInsight(context: {
  analyticsType: "power" | "cadence" | "execution" | "monthly";
  summaryData: string;
  phase: string;
  ftpWatts?: number;
  ctl?: number;
  cacheDate: string;
}): Promise<string | null> {
  const cacheKey = `analytics-${context.analyticsType}-${context.cacheDate}-${context.summaryData.slice(0, 30).replace(/\s/g, "")}`;
  const cached = readCache(cacheKey);
  if (cached) return cached;
  try {
    const response = await coachFetch({
      workoutName: "analytics",
      analyticsType: context.analyticsType,
      summaryData: context.summaryData,
      phase: context.phase,
      ftpWatts: context.ftpWatts,
      ctl: context.ctl,
      mode: "analytics",
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { tip?: unknown };
    const tip = typeof data.tip === "string" ? data.tip.trim() : null;
    if (!tip) return null;
    writeCache(cacheKey, tip);
    return tip;
  } catch {
    return null;
  }
}

// ── fetchOnboardingWelcome ────────────────────────────────────────────────────

export async function fetchOnboardingWelcome(context: {
  ftpWatts?: number;
  goal: string;
  experienceLevel: string;
  availableDays: number;
  durationMin: number;
  hasPowerMeter: boolean;
}): Promise<string | null> {
  try {
    const response = await coachFetch({
      workoutName: "onboarding-welcome",
      ftpWatts: context.ftpWatts,
      goal: context.goal,
      level: context.experienceLevel,
      availableDays: context.availableDays,
      durationMin: context.durationMin,
      hasPowerMeter: context.hasPowerMeter,
      phase: "base",
      mode: "onboarding-welcome",
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { tip?: unknown };
    return typeof data.tip === "string" ? data.tip.trim() : null;
  } catch {
    return null;
  }
}

// ── fetchPmcSummary ───────────────────────────────────────────────────────────

export async function fetchPmcSummary(context: {
  ctl: number;
  atl: number;
  tsb: number;
  ctlDelta?: number;
  phase: string;
  ftpWatts?: number;
  cacheDate: string;
}): Promise<string | null> {
  const cacheKey = `pmc-summary-${context.cacheDate}-${Math.round(context.ctl)}-${Math.round(context.tsb)}`;
  const cached = readCache(cacheKey);
  if (cached) return cached;
  try {
    const response = await coachFetch({
      workoutName: "pmc-summary",
      mode: "pmc-summary",
      ctl: context.ctl,
      atl: context.atl,
      tsb: context.tsb,
      ctlDelta: context.ctlDelta,
      phase: context.phase,
      ftpWatts: context.ftpWatts,
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { tip?: unknown };
    const tip = typeof data.tip === "string" ? data.tip.trim() : null;
    if (!tip) return null;
    writeCache(cacheKey, tip);
    return tip;
  } catch {
    return null;
  }
}

// ── fetchFtpUpdate ────────────────────────────────────────────────────────────

export async function fetchFtpUpdate(context: {
  newFtp: number;
  oldFtp?: number;
  testType?: string;
  phase: string;
}): Promise<string | null> {
  // No cache — one-time event per FTP test
  try {
    const response = await coachFetch({
      workoutName: "ftp-update",
      mode: "ftp-update",
      newFtp: context.newFtp,
      oldFtp: context.oldFtp,
      testType: context.testType,
      phase: context.phase,
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { tip?: unknown };
    return typeof data.tip === "string" ? data.tip.trim() : null;
  } catch {
    return null;
  }
}

// ── fetchTaperCheck ───────────────────────────────────────────────────────────

/**
 * Fetch a 2-sentence AI taper check for race week.
 * Warns if hard sessions are planned within 3 days of the race.
 * Returns null on any error.
 */
export async function fetchTaperCheck(context: {
  eventId: string;
  daysToEvent: number;
  hardWorkoutDaysBeforeRace?: string;
  ftpWatts?: number;
  tsb?: number;
  ctl?: number;
  phase: string;
}): Promise<string | null> {
  const cacheKey = `taper-check-${context.eventId}-${context.daysToEvent}-${context.hardWorkoutDaysBeforeRace ?? "none"}`;
  const cached = readCache(cacheKey);
  if (cached) return cached;
  try {
    const response = await coachFetch({
      workoutName: "taper-check",
      phase: context.phase,
      daysToEvent: context.daysToEvent,
      hardWorkoutDaysBeforeRace: context.hardWorkoutDaysBeforeRace,
      ftpWatts: context.ftpWatts,
      tsb: context.tsb,
      ctl: context.ctl,
      mode: "taper-check",
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { tip?: unknown };
    const tip = typeof data.tip === "string" ? data.tip.trim() : null;
    if (!tip) return null;
    writeCache(cacheKey, tip);
    return tip;
  } catch {
    return null;
  }
}

// ── fetchGoalInterview ────────────────────────────────────────────────────────

/**
 * Fetch a 2-3 sentence AI coaching note after goal selection in onboarding.
 * Returns null on any error (non-blocking).
 */
export async function fetchGoalInterview(context: {
  goal: string;
  experienceLevel: string;
  hoursPerWeek?: number;
  trainingHistoryYears?: number;
  upcomingRaceDescription?: string;
  availableDays?: number;
  ftpWatts?: number;
  phase: string;
}): Promise<string | null> {
  try {
    const response = await coachFetch({
      workoutName: `goal: ${context.goal}`,
      goal: context.goal,
      level: context.experienceLevel,
      hoursPerWeek: context.hoursPerWeek,
      trainingHistoryYears: context.trainingHistoryYears,
      upcomingRaceDescription: context.upcomingRaceDescription,
      availableDays: context.availableDays,
      ftpWatts: context.ftpWatts,
      phase: context.phase,
      mode: "goal-interview",
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { tip?: unknown };
    return typeof data.tip === "string" ? data.tip.trim() : null;
  } catch {
    return null;
  }
}

// ── fetchPostRideTip ─────────────────────────────────────────────────────────

/**
 * Fetch a 2-sentence reflective AI coaching note after a workout is completed.
 * Cached per workoutId so it's generated once per ride.
 *
 * Returns null on any error.
 */
export async function fetchPostRideTip(
  context: PostRideContext,
): Promise<string | null> {
  const cacheKey = `post-ride-tip-${context.workoutId}`;

  const cached = readCache(cacheKey);
  if (cached) return cached;

  try {
    const response = await coachFetch({
      workoutName: context.workoutName,
      workoutType: context.workoutType,
      durationMin: context.actualDurationMinutes ?? 60,
      phase: context.phase,
      ftpWatts: context.ftpWatts,
      tsb: context.tsb,
      avgRpe: context.rpe,
      dayClassification: context.dayClassification,
      mode: "post-ride",
    });

    if (!response.ok) return null;

    const data = (await response.json()) as { tip?: unknown };
    const tip = typeof data.tip === "string" ? data.tip.trim() : null;
    if (!tip) return null;

    writeCache(cacheKey, tip);
    return tip;
  } catch {
    return null;
  }
}
